import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Download as DownloadIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import axios from 'axios';

function DatabaseBackup() {
  const [tables, setTables] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadTables();
    loadBackups();
  }, []);

  const loadTables = async () => {
    try {
      const response = await axios.get('/api/DatabaseBackup/tables');
      setTables(response.data);
    } catch (error) {
      console.error('Error loading tables:', error);
      setMessage({ type: 'error', text: 'Hiba a táblák betöltésekor' });
    }
  };

  const loadBackups = async () => {
    try {
      const response = await axios.get('/api/DatabaseBackup/backups');
      setBackups(response.data);
    } catch (error) {
      console.error('Error loading backups:', error);
    }
  };

  const handleTableToggle = (tableName) => {
    setSelectedTables(prev => 
      prev.includes(tableName)
        ? prev.filter(t => t !== tableName)
        : [...prev, tableName]
    );
  };

  const handleSelectAll = () => {
    if (selectedTables.length === tables.length) {
      setSelectedTables([]);
    } else {
      setSelectedTables(tables.map(t => t.name));
    }
  };

  const handleBackup = async () => {
    if (selectedTables.length === 0) {
      setMessage({ type: 'warning', text: 'Válassz ki legalább egy táblát!' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await axios.post('/api/DatabaseBackup/backup', {
        tables: selectedTables
      });

      setMessage({ 
        type: 'success', 
        text: `Mentés sikeres! Fájl: ${response.data.fileName}` 
      });
      
      loadBackups();
      
      // Download the backup file via axios (with auth header)
      const downloadRes = await axios.get(`/api/DatabaseBackup/download/${response.data.fileName}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([downloadRes.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Backup error:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Hiba a mentés során' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleRestore = async () => {
    if (!selectedFile) {
      setMessage({ type: 'warning', text: 'Válassz ki egy fájlt!' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      // Use direct API URL (bypasses nginx) if set at build time via REACT_APP_DIRECT_API_URL
      const directApiBase = process.env.REACT_APP_DIRECT_API_URL || '';
      const restoreUrl = directApiBase
        ? `${directApiBase}/api/DatabaseBackup/restore`
        : '/api/DatabaseBackup/restore';

      await axios.post(restoreUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setRestoreDialogOpen(false);
      setSelectedFile(null);
      window.dispatchEvent(new Event('restore-started'));
      
    } catch (error) {
      console.error('Restore error:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Hiba a visszaállítás során' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBackup = async (fileName) => {
    try {
      const response = await axios.get(`/api/DatabaseBackup/download/${fileName}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      setMessage({ type: 'error', text: 'Hiba a fájl letöltésekor' });
    }
  };

  const handleDeleteBackup = async (fileName) => {
    if (!window.confirm(`Biztosan törölni szeretnéd ezt a mentést: ${fileName}?`)) {
      return;
    }

    try {
      await axios.delete(`/api/DatabaseBackup/backups/${fileName}`);
      setMessage({ type: 'success', text: 'Mentés törölve' });
      loadBackups();
    } catch (error) {
      console.error('Delete error:', error);
      setMessage({ type: 'error', text: 'Hiba a törlés során' });
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('hu-HU');
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StorageIcon /> Adatbázis Mentés
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Táblák kiválasztása */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Táblák kiválasztása</Typography>
              <Button 
                size="small" 
                onClick={handleSelectAll}
                variant="outlined"
              >
                {selectedTables.length === tables.length ? 'Kijelölés törlése' : 'Összes kijelölése'}
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <FormGroup>
              {tables.map((table) => (
                <FormControlLabel
                  key={table.name}
                  control={
                    <Checkbox
                      checked={selectedTables.includes(table.name)}
                      onChange={() => handleTableToggle(table.name)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>{table.name}</Typography>
                      <Chip label={table.size} size="small" />
                      <Chip label={`${table.columnCount} oszlop`} size="small" variant="outlined" />
                    </Box>
                  }
                />
              ))}
            </FormGroup>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleBackup}
                disabled={loading || selectedTables.length === 0}
                fullWidth
              >
                {loading ? 'Mentés...' : `Mentés (${selectedTables.length} tábla)`}
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                startIcon={<UploadIcon />}
                onClick={() => setRestoreDialogOpen(true)}
                disabled={loading}
              >
                Visszaállítás
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Korábbi mentések */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Korábbi mentések</Typography>
              <IconButton onClick={loadBackups} size="small">
                <RefreshIcon />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {backups.length === 0 ? (
              <Typography color="text.secondary">Nincs elérhető mentés</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fájlnév</TableCell>
                      <TableCell>Méret</TableCell>
                      <TableCell>Dátum</TableCell>
                      <TableCell align="right">Műveletek</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backups.map((backup) => (
                      <TableRow key={backup.fileName}>
                        <TableCell>{backup.fileName}</TableCell>
                        <TableCell>{formatBytes(backup.size)}</TableCell>
                        <TableCell>{formatDate(backup.created)}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadBackup(backup.fileName)}
                            title="Letöltés"
                          >
                            <DownloadIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteBackup(backup.fileName)}
                            title="Törlés"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Visszaállítás Dialog */}
      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adatok visszaállítása</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            FIGYELEM! A visszaállítás felülírja a jelenlegi adatokat!
          </Alert>

          <input
            type="file"
            accept=".sql"
            onChange={handleFileSelect}
            style={{ marginTop: 16 }}
          />

          {selectedFile && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Kiválasztott fájl: {selectedFile.name} ({formatBytes(selectedFile.size)})
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)}>Mégse</Button>
          <Button
            onClick={handleRestore}
            variant="contained"
            color="primary"
            disabled={!selectedFile || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {loading ? 'Visszaállítás...' : 'Visszaállítás'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DatabaseBackup;
